import { useEffect, useRef } from "react"
import { registerBackHandler } from "@/utils/backButton"
import type { BackHandler } from "@/utils/backButton"

/**
 * Claims the Android back button while something is on screen — a modal, a
 * dropdown, any overlay whose own close button is what a back press should
 * really be pressing.
 *
 * `handler` returns true to consume the press. Handlers run newest-first, so a
 * dialog opened on top of another closes first and the app only falls through
 * to the page-level rules (NativeBackHandler) once nothing is left.
 *
 * The handler is read through a ref, so it can close over fresh props and state
 * without re-registering — which would otherwise shuffle it to the top of the
 * stack on every render and break that ordering.
 */
export const useBackHandler = (handler: BackHandler, enabled = true) => {
  const latest = useRef(handler)
  latest.current = handler

  useEffect(() => {
    if (!enabled) return
    return registerBackHandler(() => latest.current())
  }, [enabled])
}

export default useBackHandler
